import { Icon } from '@iconify/react'
import { useSnackbar } from 'notistack5'
import { useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import settings2Fill from '@iconify/icons-eva/settings-2-fill'
import peopleFill from '@iconify/icons-eva/people-fill'
import activityFill from '@iconify/icons-eva/activity-fill'
import sunFill from '@iconify/icons-eva/sun-fill'
import moonFill from '@iconify/icons-eva/moon-fill'
import arrowIosDownwardFill from '@iconify/icons-eva/arrow-ios-downward-fill'
import roundFullscreen from '@iconify/icons-ic/round-fullscreen'
import roundFullscreenExit from '@iconify/icons-ic/round-fullscreen-exit'
import { Link as RouterLink, useNavigate } from 'react-router-dom'
// material
import { alpha } from '@material-ui/core/styles'
import {
  Avatar,
  Button,
  ButtonBase,
  Box,
  Divider,
  MenuItem,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@material-ui/core'
// routes
import { PATH_DASHBOARD, PATH_AUTH } from '../../routes/paths'
// hooks
import useAuth from '../../hooks/useAuth'
import useIsMountedRef from '../../hooks/useIsMountedRef'
import useSettings from '../../hooks/useSettings'
// components
import MenuPopover from '../../components/MenuPopover'

// ----------------------------------------------------------------------

const MENU_OPTIONS = [
  // {
  //   label: 'Profile',
  //   icon: personFill,
  //   linkTo: PATH_DASHBOARD.user.profile,
  // },
  // {
  //   label: 'Files',
  //   icon: fileTextFill,
  //   linkTo: PATH_DASHBOARD.general.files,
  // },
  // TODO: Don't show users if the user is not admin
  {
    label: 'Organisation',
    icon: peopleFill,
    linkTo: PATH_DASHBOARD.user.list,
  },
  {
    label: 'Settings',
    icon: settings2Fill,
    linkTo: PATH_DASHBOARD.user.account,
  },
]

const MENU_ITEM_SX = { typography: 'body2', py: 1, px: 2.5 }
const MENU_ICON_SX = { mr: 1.5, width: 20, height: 20, color: 'text.secondary' }

const getInitials = (name, email) => {
  const parts = (name || '').trim().split(/\s+/).filter(Boolean)
  if (parts.length > 0) {
    return parts
      .slice(0, 2)
      .map((part) => part[0])
      .join('')
      .toUpperCase()
  }
  return email ? email[0].toUpperCase() : '?'
}

// ----------------------------------------------------------------------

AccountPopover.propTypes = {
  name: PropTypes.string,
  organisation: PropTypes.string,
  email: PropTypes.string,
}

export default function AccountPopover({ name, organisation, email }) {
  const anchorRef = useRef(null)
  const navigate = useNavigate()
  const { enqueueSnackbar } = useSnackbar()
  const isMountedRef = useIsMountedRef()
  const { user, logout } = useAuth()
  const { themeMode, onChangeMode } = useSettings()
  const [open, setOpen] = useState(false)

  const displayEmail = email || user?.email
  const displayName = name || user?.displayName || displayEmail || 'Account'
  const initials = getInitials(name || user?.displayName, displayEmail)

  const handleOpen = () => {
    setOpen(true)
  }
  const handleClose = () => {
    setOpen(false)
  }

  // Full screen used to live in the Settings drawer next to the mode switch.
  const [isFullscreen, setIsFullscreen] = useState(false)
  useEffect(() => {
    const handleFullscreenChange = () => setIsFullscreen(!!document.fullscreenElement)
    document.addEventListener('fullscreenchange', handleFullscreenChange)
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange)
  }, [])
  const handleToggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen()
    } else if (document.exitFullscreen) {
      document.exitFullscreen()
    }
    handleClose()
  }

  const handleChangeMode = (event, mode) => {
    // ToggleButtonGroup reports null when the selected button is clicked again.
    if (mode) {
      onChangeMode({ target: { value: mode } })
    }
  }

  const handleLogout = async () => {
    try {
      await logout()
      if (isMountedRef.current) {
        handleClose()
      }
      navigate(PATH_AUTH.login)
    } catch (error) {
      console.error(error)
      enqueueSnackbar('Unable to logout', { variant: 'error' })
    }
  }

  return (
    <>
      <Tooltip title="Account">
        <ButtonBase
          ref={anchorRef}
          onClick={handleOpen}
          aria-label="Account menu"
          aria-haspopup="true"
          sx={{
            height: 36,
            gap: 1,
            pl: 0.5,
            pr: { xs: 0.5, lg: 1 },
            borderRadius: 1,
            color: 'text.primary',
            bgcolor: open ? 'action.selected' : 'transparent',
            '&:hover': { bgcolor: open ? 'action.selected' : 'action.hover' },
          }}
        >
          <Avatar
            sx={{
              width: 28,
              height: 28,
              fontSize: 12,
              fontWeight: 600,
              color: 'primary.main',
              bgcolor: (theme) => alpha(theme.palette.primary.main, 0.16),
            }}
          >
            {initials}
          </Avatar>
          <Typography
            variant="body2"
            noWrap
            sx={{ display: { xs: 'none', lg: 'block' }, maxWidth: 180, fontWeight: 'fontWeightMedium' }}
          >
            {displayName}
          </Typography>
          <Box
            component={Icon}
            icon={arrowIosDownwardFill}
            sx={{ display: { xs: 'none', lg: 'block' }, width: 16, height: 16, color: 'text.secondary' }}
          />
        </ButtonBase>
      </Tooltip>

      <MenuPopover open={open} onClose={handleClose} anchorEl={anchorRef.current} sx={{ width: 280 }}>
        <Box sx={{ px: 2.5, py: 2 }}>
          <Typography variant="subtitle2" noWrap title={displayName}>
            {displayName}
          </Typography>
          {organisation && (
            <Typography variant="body2" sx={{ color: 'text.secondary' }} noWrap title={organisation}>
              {organisation}
            </Typography>
          )}
          {displayEmail && displayEmail !== displayName && (
            <Typography variant="body2" sx={{ color: 'text.secondary' }} noWrap title={displayEmail}>
              {displayEmail}
            </Typography>
          )}
        </Box>

        <Divider />

        <Box sx={{ py: 1 }}>
          {MENU_OPTIONS.map((option) => (
            <MenuItem
              key={option.label}
              to={option.linkTo}
              component={RouterLink}
              onClick={handleClose}
              sx={MENU_ITEM_SX}
            >
              <Box component={Icon} icon={option.icon} sx={MENU_ICON_SX} />
              {option.label}
            </MenuItem>
          ))}
          {user?.is_superuser && (
            <MenuItem
              key="pipelines"
              to={PATH_DASHBOARD.pipelines}
              component={RouterLink}
              onClick={handleClose}
              sx={MENU_ITEM_SX}
            >
              <Box component={Icon} icon={activityFill} sx={MENU_ICON_SX} />
              Pipelines
            </MenuItem>
          )}
          <MenuItem onClick={handleToggleFullscreen} sx={MENU_ITEM_SX}>
            <Box component={Icon} icon={isFullscreen ? roundFullscreenExit : roundFullscreen} sx={MENU_ICON_SX} />
            {isFullscreen ? 'Exit full screen' : 'Full screen'}
          </MenuItem>
        </Box>

        <Divider />

        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 2.5, py: 1.5 }}>
          <Typography variant="body2">Appearance</Typography>
          <ToggleButtonGroup
            exclusive
            size="small"
            value={themeMode}
            onChange={handleChangeMode}
            aria-label="Appearance"
            sx={{
              '& .MuiToggleButton-root': {
                gap: 0.5,
                px: 1.25,
                py: 0.5,
                typography: 'caption',
                fontWeight: 'fontWeightMedium',
                color: 'text.secondary',
                borderColor: 'divider',
                '&.Mui-selected, &.Mui-selected:hover': {
                  color: 'text.primary',
                  bgcolor: 'action.selected',
                },
              },
            }}
          >
            <ToggleButton value="light" aria-label="Light mode">
              <Box component={Icon} icon={sunFill} sx={{ width: 16, height: 16 }} />
              Light
            </ToggleButton>
            <ToggleButton value="dark" aria-label="Dark mode">
              <Box component={Icon} icon={moonFill} sx={{ width: 16, height: 16 }} />
              Dark
            </ToggleButton>
          </ToggleButtonGroup>
        </Stack>

        <Divider />

        <Box sx={{ p: 1.5 }}>
          <Button fullWidth color="inherit" variant="outlined" onClick={handleLogout}>
            Log out
          </Button>
        </Box>
      </MenuPopover>
    </>
  )
}
